import React, { forwardRef } from 'react';
import {
  Alert as MuiAlert,
  AlertProps as MuiAlertProps,
  AlertTitle as MuiAlertTitle,
  AlertTitleProps as MuiAlertTitleProps,
} from '@mui/material';

/**
 * Props for the Alert component.
 *
 * Extends all MUI Alert props with enhanced accessibility features.
 *
 * @example
 * ```tsx
 * <Alert severity="success">Operation completed successfully!</Alert>
 * <Alert severity="error" onClose={handleClose}>An error occurred</Alert>
 * <Alert severity="warning">
 *   <AlertTitle>Warning</AlertTitle>
 *   This is a warning message
 * </Alert>
 * ```
 */
export interface AlertProps extends MuiAlertProps {
  /**
   * Accessible label for screen readers.
   * If not provided, automatically generated from severity (e.g., "Success alert", "Error alert").
   */
  'aria-label'?: string;
}

/**
 * Props for the AlertTitle component.
 *
 * A Typography component specifically for use as a title within Alert.
 */
export type AlertTitleProps = MuiAlertTitleProps;

/**
 * Alert component for displaying notification messages with different severity levels.
 *
 * A thin wrapper around MUI Alert with enhanced accessibility features including
 * automatic aria-label generation based on severity and proper ARIA role attributes.
 *
 * Features:
 * - Multiple severity levels: success, info, warning, error
 * - Visual variants: standard, filled, outlined
 * - Optional close button with onClose handler
 * - Custom icons support
 * - AlertTitle for structured alerts
 * - Full theme integration
 * - Enhanced accessibility
 *
 * @component
 * @example
 * ```tsx
 * // Simple alert
 * <Alert severity="success">Success message</Alert>
 *
 * // With title
 * <Alert severity="warning">
 *   <AlertTitle>Warning</AlertTitle>
 *   Detailed warning message
 * </Alert>
 *
 * // With close button
 * <Alert severity="error" onClose={() => console.log('closed')}>
 *   Error message
 * </Alert>
 *
 * // Custom variant
 * <Alert severity="info" variant="outlined">
 *   Outlined info alert
 * </Alert>
 * ```
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  ({ severity = 'info', onClose, 'aria-label': ariaLabel, ...props }, ref) => {
    // Generate default aria-label from severity
    const defaultAriaLabel = React.useMemo(() => {
      if (ariaLabel !== undefined) return ariaLabel;
      const severityText = severity.charAt(0).toUpperCase() + severity.slice(1);
      return `${severityText} alert`;
    }, [severity, ariaLabel]);

    // Determine ARIA role based on severity
    // error/warning = 'alert' (assertive), success/info = 'status' (polite)
    const role = React.useMemo(
      () => (severity === 'error' || severity === 'warning' ? 'alert' : 'status'),
      [severity]
    );

    // Enhanced close button with aria-label
    const closeButtonAriaLabel = 'Close alert';

    return (
      <MuiAlert
        ref={ref}
        severity={severity}
        onClose={onClose}
        aria-label={defaultAriaLabel}
        role={role}
        slotProps={{
          closeButton: onClose ? { 'aria-label': closeButtonAriaLabel } : undefined,
        }}
        {...props}
      />
    );
  }
);

Alert.displayName = 'Alert';

/**
 * AlertTitle component for use within Alert to provide a title.
 *
 * A thin wrapper around MUI AlertTitle with no additional logic.
 * Inherits all Typography props for flexible styling.
 *
 * @component
 * @example
 * ```tsx
 * <Alert severity="warning">
 *   <AlertTitle>Warning</AlertTitle>
 *   This is the alert content
 * </Alert>
 * ```
 */
export const AlertTitle = forwardRef<HTMLDivElement, AlertTitleProps>((props, ref) => (
  <MuiAlertTitle ref={ref} {...props} />
));

AlertTitle.displayName = 'AlertTitle';
